import 'date-fns'
import React, { useContext } from 'react'
import styled from '@emotion/styled'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import {
  differenceInDays,
  isPast,
  isValid,
  parseISO,
  differenceInCalendarDays,
} from 'date-fns'
import { useStore } from '../../Store'
import axios from 'axios'
import {
  QA_PROXY_ENDPOINT,
  PRODUCTION_ENDPOINT,
} from '../../Constants/endpoints'
import { isToday } from 'date-fns/esm'
import { ConfigContext } from '../../Store/ConfigContext.js'
import { getCredentials } from '../../Utils'

const LegContainer = styled('div')(({ theme }) => ({
  fontSize: '.75em',
  width: '100%',
  color: theme.palette.primary.main,
  gridArea: '1 / 2 / 2 / 4',
  marginTop: '2rem',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0 3rem',
    marginBottom: '1rem',
  },
  [theme.breakpoints.down('xs')]: {
    padding: '0 1rem',
  },
}))

const LegBorderStyle = styled('div')(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.primary.main}`,
  paddingRight: '1rem',
  paddingLeft: '1rem',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr 1fr',
  gridGap: '10px',
  [theme.breakpoints.down('sm')]: {
    marginTop: '1rem',
  },
  [theme.breakpoints.down('xs')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

const InputStyle = styled(TextField)({
  width: '100%',
  marginTop: '.5rem',
  '& div': {
    height: '40px',
  },
})

const TitleContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0 3rem',
  },
  [theme.breakpoints.down('xs')]: {
    padding: '0 1rem',
  },
}))

function LegItem({ leg, formikProps, setRoundTrip, isRoundTrip }) {
  const { values, handleChange, errors, setFieldValue } = formikProps
  const { ops } = useContext(ConfigContext)
  const { setRushService, setAlertMessage } = useStore((state) => ({
    setRushService: state.setRushService,
    setAlertMessage: state.setAlertMessage,
  }))

  React.useEffect(() => {
    console.log('OPS ACCESS: ', ops)
  }, [ops])

  const errorMessages = {
    pastDate: 'Invalid date. You should select a future date',
    shortNotice:
      'Due to the short notice please contact us at: U.S. Tel. +1 (786) 206.6147 or Mex.Tel. +52 (222) 944.3402',
    rushService:
      'For permits requested within 72 hours of flight, Cuba charges an additional fee plus and CST applies an Admin fee based on the number of days available. This rush fee will be displayed on the next screen.',
    invalidDate: 'Invalid date. Please try again',
    leg2BeforeLeg1: 'Invalid date. Leg 2 should happen after Leg 1',
  }

  const handleDateChange = (item) => {
    handleChange(item)
  }
  const validateDate = (item, setFieldValue, values) => {
    const today = new Date()
    const departureDate = parseISO(item.target.value)

    const isFuture = !isPast(departureDate) || isToday(departureDate)
    const isValidDate = isValid(departureDate)

    const daysDifference = differenceInCalendarDays(departureDate, today)
    const isRush = isValidDate && daysDifference <= 3

    // codigo comentado el 2 de junio de 2025 si este codigo no se requiere despues del 2 de julio de 2025 eliminar
    // este codigo se comento por cambios en los lineamientos del calculo de schedules permitidos de COF.
    // const isImpossible = isValidDate && isToday(departureDate)

    const otherDate =
      leg.number === 2
        ? parseISO(values.l1DepartureDate)
        : parseISO(values.l2DepartureDate)

    const isOtherDateRush =
      otherDate && differenceInCalendarDays(otherDate, today) <= 3

    const earlierLeg = leg.number === 2 ? otherDate : departureDate
    const laterLeg = leg.number === 2 ? departureDate : otherDate
    const diffLeg1Leg2 =
      earlierLeg && laterLeg && differenceInDays(laterLeg, earlierLeg)
    const leg2BeforeLeg1 = diffLeg1Leg2 && diffLeg1Leg2 < 0

    if (!isValidDate) {
      setAlertMessage(errorMessages.invalidDate)
      setFieldValue(`l${leg.number}DepartureDate`, '')
      return
    }

    if (!isFuture) {
      setFieldValue(`l${leg.number}DepartureDate`, '')
      setAlertMessage(errorMessages.pastDate)
      return
    }

    // codigo comentado el 2 de junio de 2025 si este codigo no se requiere despues del 2 de julio de 2025 eliminar
    // este codigo se comento por cambios en los lineamientos del calculo de schedules permitidos de COF.
    // if (isImpossible) {
    //   setAlertMessage(errorMessages.shortNotice)
    //   setFieldValue(`l${leg.number}DepartureDate`, '')
    //   return
    // }

    if (isRush) {
      !isOtherDateRush && setAlertMessage(errorMessages.rushService)
      setRushService(true)
      handleChange(item)
      return
    }

    if (leg2BeforeLeg1) {
      setAlertMessage(errorMessages.leg2BeforeLeg1)
      setFieldValue(`l${leg.number}DepartureDate`, '')
      return
    }

    //IF everything is fine
    if (!isOtherDateRush) {
      setRushService(false)
    }
    handleChange(item)
  }

  const validateAirport = async (
    airportName,
    fieldName,
    _setFieldValue,
    onBlur
  ) => {
    if (airportName?.length === 4 && !onBlur) {
      _setFieldValue(fieldName, airportName)
      const BASE_URL = PRODUCTION_ENDPOINT || QA_PROXY_ENDPOINT
      const airport = await axios.get(
        `${BASE_URL}/apt/list?aadIcaoIdFilter=${airportName}`,
        {
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
            Authorization: getCredentials(),
          },
        }
      )

      if (!airport.data.empty) {
        const foundAirport = { ...airport.data.content[0] }
        if (fieldName === `l${leg.number}DepartureAirport`) {
          _setFieldValue(`l${leg.number}DepartureLfsn`, foundAirport.lfsn)
          _setFieldValue(`l${leg.number}DepartureFir`, foundAirport.country)
        }
        if (fieldName === `l${leg.number}ArrivalAirport`) {
          _setFieldValue(`l${leg.number}ArrivalLfsn`, foundAirport.lfsn)
          _setFieldValue(`l${leg.number}ArrivalFir`, foundAirport.country)
        }
      }
      if (airport.data.empty) {
        _setFieldValue(fieldName, '')
        if (fieldName === `l${leg.number}DepartureAirport`) {
          _setFieldValue(`l${leg.number}DepartureLfsn`, '')
          _setFieldValue(`l${leg.number}DepartureFir`, '')
          _setFieldValue(`l${leg.number}DepartureAirport`, '')
          setAlertMessage('Airport not in our DB')
        }
        if (fieldName === `l${leg.number}ArrivalAirport`) {
          _setFieldValue(`l${leg.number}ArrivalLfsn`, '')
          _setFieldValue(`l${leg.number}ArrivalFir`, '')
          _setFieldValue(`l${leg.number}ArrivalAirport`, '')
          setAlertMessage('Airport not in our DB')
        }
      }
    } else {
      if (airportName?.length !== 4 && onBlur) {
        _setFieldValue(fieldName, '')
        setAlertMessage('Invalid ICAO format')
        return
      }
      _setFieldValue(fieldName, airportName)
    }
  }

  const renderGapField = () => {
    console.log('TYPE OF OPS: ', typeof ops)
    if (ops === 'true') {
      return (
        <LegBorderStyle>
          <InputStyle
            id={`l${leg.number}DeptGAP`}
            name={`l${leg.number}DeptGAP`}
            error={!!errors[`l${leg.number}DeptGAP`]}
            helperText={
              errors[`l${leg.number}DeptGAP`] && errors[`l${leg.number}DeptGAP`]
            }
            label="DeptGAP"
            focused
            type="number"
            value={values[`l${leg.number}DeptGAP`]}
            onChange={(event) => {
              handleChange(event)
            }}
            onBlur={(e) => {
              const { id, value } = e.target
              setFieldValue(id, value || 0)
            }}
          />
          <InputStyle
            id={`l${leg.number}ArrGAP`}
            name={`l${leg.number}ArrGAP`}
            error={!!errors[`l${leg.number}ArrGAP`]}
            helperText={
              errors[`l${leg.number}ArrGAP`] && errors[`l${leg.number}ArrGAP`]
            }
            label="ArrGAP"
            focused
            type="number"
            value={values[`l${leg.number}ArrGAP`]}
            onChange={(event) => {
              handleChange(event)
            }}
            onBlur={(e) => {
              const { id, value } = e.target
              setFieldValue(id, value || 0)
            }}
          />
        </LegBorderStyle>
      )
    }
  }

  return (
    <LegContainer>
      <TitleContainer>
        <h2 style={{ fontSize: '1rem ' }}>{`Leg ${leg.number}`}</h2>
        <span />
        {leg.number === 1 && (
          <Button
            variant="contained"
            onClick={setRoundTrip}
            color="secondary"
            style={{ marginRight: 20 }}
          >
            {isRoundTrip ? 'One way trip' : 'Round trip'}
          </Button>
        )}
      </TitleContainer>
      <LegBorderStyle>
        <InputStyle
          id={`l${leg.number}DepartureAirport`}
          name={`l${leg.number}DepartureAirport`}
          error={!!errors[`l${leg.number}DepartureAirport`]}
          helperText={
            errors[`l${leg.number}DepartureAirport`] &&
            errors[`l${leg.number}DepartureAirport`]
          }
          label="Departure Airport (ICAO)*"
          focused
          onBlur={(e) =>
            validateAirport(
              e.target.value.toUpperCase(),
              e.target.name,
              setFieldValue,
              true
            )
          }
          value={(values[`l${leg.number}DepartureAirport`] || '').toUpperCase()}
          onChange={(e) =>
            validateAirport(
              e.target.value.toUpperCase(),
              e.target.name,
              setFieldValue
            )
          }
        />
        <InputStyle
          id={`l${leg.number}ArrivalAirport`}
          name={`l${leg.number}ArrivalAirport`}
          error={!!errors[`l${leg.number}ArrivalAirport`]}
          helperText={
            errors[`l${leg.number}ArrivalAirport`] &&
            errors[`l${leg.number}ArrivalAirport`]
          }
          label="Arrival Airport (ICAO)*"
          focused
          value={(values[`l${leg.number}ArrivalAirport`] || '').toUpperCase()}
          onBlur={(e) => {
            validateAirport(e.target.value, e.target.name, setFieldValue, true)
            if (
              values.l1DepartureDate &&
              values.l2DepartureDate &&
              values.l2DepartureDate === values.l1DepartureDate
            ) {
              setAlertMessage(
                'The time gap between trips might be tight, please take into consideration flight times'
              )
            }
          }}
          onChange={(e) =>
            validateAirport(
              e.target.value.toUpperCase(),
              e.target.name,
              setFieldValue
            )
          }
        />
        <InputStyle
          id={`l${leg.number}DepartureDate`}
          name={`l${leg.number}DepartureDate`}
          error={!!errors[`l${leg.number}DepartureDate`]}
          helperText={
            errors[`l${leg.number}DepartureDate`] &&
            errors[`l${leg.number}DepartureDate`]
          }
          label="Departure Date *"
          type="date"
          value={values[`l${leg.number}DepartureDate`]}
          onChange={(e) => handleDateChange(e)}
          onBlur={(e) => {
            validateDate(e, setFieldValue, values)
            if (
              values.l1DepartureDate &&
              values.l2DepartureDate &&
              values.l2DepartureDate === values.l1DepartureDate
            ) {
              setAlertMessage(
                'The time gap between trips might be tight, please take into consideration flight times'
              )
            }
          }}
          focused
        />
        <InputStyle
          id={`l${leg.number}ETDZulu`}
          name={`l${leg.number}ETDZulu`}
          error={!!errors[`l${leg.number}ETDZulu`]}
          helperText={
            errors[`l${leg.number}ETDZulu`] && errors[`l${leg.number}ETDZulu`]
          }
          label="ETD Zulu Time*"
          focused
          value={values[`l${leg.number}ETDZulu`]}
          onChange={(event) => {
            handleChange(event)
          }}
          onBlur={(e) => {
            const { id, value } = e.target
            setFieldValue(id, value.padStart(4, '0'))
          }}
        />
      </LegBorderStyle>
      {renderGapField()}
    </LegContainer>
  )
}

export default LegItem
